import { Payment } from '../../models';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { AbstractPaymentService } from '../services/abstract-payment.service';
import { iif, of, pipe, Subject, withLatestFrom } from 'rxjs';
import { map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';

export interface PriceContainerState {
  payments: Payment[];
  paymentableId: number;
  charges: { charge: number; tax_rate: number }[];
  deliveryCharges: { charge: number; tax_rate: number }[];
  loading: boolean;
  submitting: boolean;
  discount: number;
}

const defaultState: PriceContainerState = {
  payments: [],
  paymentableId: null,
  charges: [],
  deliveryCharges: [],
  loading: true,
  submitting: false,
  discount: 0
};

@Injectable()
export class PriceContainerStore extends ComponentStore<PriceContainerState> {
  pamyentsChanged = new Subject<void>();

  constructor(private paymentService: AbstractPaymentService) {
    super(defaultState);
  }

  private readonly loading$ = this.select((state) => state.loading);
  private readonly payments$ = this.select((state) =>
    state.payments.filter(({ deleted }) => Boolean(deleted) === false)
  );
  private readonly paymentsIncludingDeleted$ = this.select(
    (state) => state.payments
  );
  private readonly itemsSubTotal$ = this.select((state) =>
    state.charges.reduce((acc, charge) => acc + charge.charge, 0)
  );
  private readonly deliverySubTotal$ = this.select((state) =>
    state.deliveryCharges.reduce((acc, charge) => acc + charge.charge, 0)
  );
  private readonly tax$ = this.select((state) =>
    [...state.charges, ...state.deliveryCharges].reduce(
      (acc, charge) => acc + charge.charge * charge.tax_rate,
      0
    )
  );
  private readonly total$ = this.select(
    (state) =>
      [...state.charges, ...state.deliveryCharges].reduce(
        (acc, charge) => acc + charge.charge * (charge.tax_rate + 1),
        0
      ) - (state.discount ?? 0)
  );
  private readonly totalPaid$ = this.select(this.payments$, (payments) =>
    payments
      .filter(({ deleted }) => Boolean(deleted) === false)
      .reduce((acc, payment) => acc + payment.amount, 0)
  );
  private readonly balance$ = this.select(
    this.total$,
    this.totalPaid$,
    (total, totalPaid) => total - totalPaid
  );

  readonly paymentableId$ = this.select((state) => state.paymentableId);
  readonly discount$ = this.select((state) => state.discount);
  readonly vm$ = this.select(
    this.loading$,
    this.payments$.pipe(
      map((payments) =>
        payments.map((payment) => ({
          ...payment,
          amount: payment.amount
        }))
      )
    ),
    this.itemsSubTotal$,
    this.deliverySubTotal$,
    this.tax$,
    this.total$,
    this.totalPaid$,
    this.balance$,
    this.discount$,
    (
      loading,
      payments,
      itemsSubTotal,
      deliverySubTotal,
      tax,
      total,
      totalPaid,
      balance,
      discount
    ) => ({
      loading,
      payments,
      itemsSubTotal,
      deliverySubTotal,
      tax,
      total,
      totalPaid,
      balance,
      discount
    })
  );

  readonly setCharges = this.updater(
    (state, charges: { charge: number; tax_rate: number }[]) => ({
      ...state,
      charges
    })
  );

  readonly setDeliveryCharges = this.updater(
    (state, charges: { charge: number; tax_rate: number }[]) => ({
      ...state,
      deliveryCharges: charges
    })
  );

  readonly setDiscount = this.updater((state, discount: number) => ({
    ...state,
    discount
  }));

  private readonly appendPayment = this.updater((state, payment: Payment) => ({
    ...state,
    payments: [...state.payments, payment]
  }));

  readonly addPayment = this.effect<{
    payment: Partial<Payment>;
    onComplete?: () => void;
  }>(
    pipe(
      tap(() => this.patchState({ submitting: true })),
      mergeMap(({ payment, onComplete }) =>
        of(payment).pipe(
          tap(() => this.patchState({ loading: true })),
          withLatestFrom(this.paymentableId$),
          switchMap(([payment, paymentableId]) =>
            this.paymentService.addPayment(paymentableId, {
              ...payment,
              amount: payment.amount * 100
            })
          ),
          tapResponse(
            (newPayment: Payment) => this.appendPayment(newPayment),
            (error) => console.error(error)
          ),
          tap(() => this.patchState({ loading: false })),
          map(() => ({ onComplete })),
          tap(() => this.pamyentsChanged.next())
        )
      ),
      tap(({ onComplete }) => onComplete && onComplete())
    )
  );

  readonly deletePayment = this.effect<number>(
    pipe(
      withLatestFrom(this.paymentableId$),
      switchMap(([paymentId, paymentableId]) =>
        this.paymentService
          .delete(paymentableId, paymentId)
          .pipe(map(() => paymentId))
      ),
      tapResponse(
        (paymentId: number) => this.setPaymentDeleted(paymentId),
        (error) => console.error(error)
      ),
      tap(() => this.pamyentsChanged.next())
    )
  );

  readonly selectPayments = this.effect<number>(
    pipe(
      tap((paymentableId) => this.patchState({ loading: true, paymentableId })),
      switchMap((paymentableId) =>
        iif(
          () => Boolean(paymentableId),
          this.paymentService.getPaymentsForDocket(paymentableId),
          of([])
        )
      ),
      tapResponse(
        (payments: Payment[]) => this.patchState({ payments, loading: false }),
        (error) => console.error(error)
      )
    )
  );

  private readonly setPaymentDeleted = this.updater(
    (state, paymentId: number) => ({
      ...state,
      payments: state.payments.map((payment) =>
        payment.id === paymentId ? { ...payment, deleted: 1 } : payment
      )
    })
  );
}
